import {
  MarketingTemporalPhasePicker,
  type TemporalPhaseTag,
} from "@/react/components/marketing/temporal";
import { MarketingFolderSelector } from "@/react/components/entityViews/files/components/editors/FileDetailModal/components/promote/components/MarketingFolderSelector";
import { cn } from "@/modern/utils";
import type { SurfaceSolutionTag } from "../api/types";
import { getAcceptReadiness } from "../utils/mergeTagIds";
import PromoteSurfaceTagPicker from "./PromoteSurfaceTagPicker";

export type ContentRoleTag = {
  id: number;
  label: string;
  description?: string | null;
};

type Props = {
  tags: SurfaceSolutionTag[];
  temporalTags: TemporalPhaseTag[];
  contentRoleTags?: ContentRoleTag[];
  solutionTagIds: number[];
  onSolutionTagIdsChange: (ids: number[]) => void;
  /** Asset / AI prefill ids — stay visible as one-tap chips. */
  suggestedSolutionTagIds?: number[];
  temporalTagId: number | null;
  onTemporalTagIdChange: (id: number | null) => void;
  contentRoleIds?: number[];
  onContentRoleIdsChange?: (ids: number[]) => void;
  disabled?: boolean;
  /** Inline amber validation under the fields (bulk sheet). Overlay uses footer hints instead. */
  showInlineHints?: boolean;
};

/**
 * Shared temporal + solution + content-role fields for overlay and bulk accept.
 * Content roles match task Promote Role step (CONTENT / folder-eligible tags).
 */
export default function PromoteSurfaceAcceptFields({
  tags,
  temporalTags,
  contentRoleTags = [],
  solutionTagIds,
  onSolutionTagIdsChange,
  suggestedSolutionTagIds = [],
  temporalTagId,
  onTemporalTagIdChange,
  contentRoleIds = [],
  onContentRoleIdsChange,
  disabled,
  showInlineHints = false,
}: Props) {
  const { needsSolutionTag, needsTemporalTag } = getAcceptReadiness({
    solutionTagIds,
    temporalTagId,
    temporalTagCount: temporalTags.length,
  });

  const toggleContentRole = (tagId: number) => {
    if (!onContentRoleIdsChange) return;
    onContentRoleIdsChange(
      contentRoleIds.includes(tagId)
        ? contentRoleIds.filter((id) => id !== tagId)
        : [...contentRoleIds, tagId]
    );
  };

  return (
    <div className={cn("space-y-4", disabled && "pointer-events-none opacity-60")}>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Temporal phase
        </div>
        <MarketingTemporalPhasePicker
          tags={temporalTags}
          value={temporalTagId}
          onChange={onTemporalTagIdChange}
          disabled={Boolean(disabled)}
        />
        {showInlineHints && needsTemporalTag && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Select a temporal phase to continue.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Solution tags
          {solutionTagIds.length > 0 && (
            <span className="ml-1.5 font-medium normal-case tracking-normal text-foreground">
              · {solutionTagIds.length} selected
            </span>
          )}
        </div>
        <PromoteSurfaceTagPicker
          tags={tags}
          value={solutionTagIds}
          onChange={onSolutionTagIdsChange}
          suggestedIds={suggestedSolutionTagIds}
        />
        {showInlineHints && needsSolutionTag && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Select at least one solution tag to continue.
          </p>
        )}
      </div>

      {contentRoleTags.length > 0 && onContentRoleIdsChange && (
        <div className="space-y-1">
          <MarketingFolderSelector
            folderTags={contentRoleTags}
            selectedTagIds={contentRoleIds}
            onToggle={toggleContentRole}
          />
        </div>
      )}
    </div>
  );
}
